/**
 * Nocturne site-wide effects. Every effect is progressive: without this script the page is complete,
 * and everything stops for prefers-reduced-motion except the night-vision toggle.
 */
(() => {
  const script = document.currentScript;
  const opts = script ? script.dataset : {};
  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const designMode = opts.designMode === 'true';

  /* Night vision: a red multiply layer over the whole site, like an astronomer's red torch. */
  const redToggle = document.querySelector('[data-nx-redmode]');
  if (redToggle) {
    const set = (on) => {
      root.classList.toggle('nx-red', on);
      redToggle.setAttribute('aria-pressed', String(on));
      try { localStorage.setItem('nx-red', on ? '1' : '0'); } catch (e) {}
    };
    try { if (localStorage.getItem('nx-red') === '1') set(true); } catch (e) {}
    redToggle.addEventListener('click', () => set(!root.classList.contains('nx-red')));
  }

  /* Menu links to a part of the page already open (e.g. "Nocturne Ciel" → /#abonnement on the home page):
     the theme leaves its phone menu open over the page, so close it. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('header-drawer a[href*="#"]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.pathname === location.pathname && url.hash) link.closest('header-drawer')?.close?.();
  });

  /* Product card titles: "Model · descriptor" reads heavy in a grid, so show the model name and set
     the descriptor on its own quieter line. Re-runs when cards are added (recommendations, filters). */
  const splitTitles = (root) => {
    root.querySelectorAll('product-card').forEach((card) => {
      if (card.dataset.nxSplit) return;
      // Cards carry the title twice (normal and zoomed-out grid views), so split every copy.
      const nodes = [];
      const walker = document.createTreeWalker(card, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) if (walker.currentNode.textContent.includes(' · ')) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const i = node.textContent.indexOf(' · ');
        const sub = document.createElement('span');
        sub.className = 'nx-card-sub';
        sub.textContent = node.textContent.slice(i + 3);
        node.textContent = node.textContent.slice(0, i);
        node.after(sub);
      });
      if (nodes.length) card.dataset.nxSplit = '1';
    });
  };
  splitTitles(document);
  new MutationObserver(() => splitTitles(document)).observe(document.body, { childList: true, subtree: true });

  /* Scroll progress thread. Horizon scrolls .page-wrapper on desktop and the document on mobile,
     so listen in the capture phase (scroll doesn't bubble) and measure whichever one is scrolling. */
  const bar = document.querySelector('.nx-progress span');
  if (bar) {
    let ticking = false;
    const scroller = () => {
      const pw = document.querySelector('.page-wrapper');
      return pw && pw.scrollHeight > pw.clientHeight + 1 && getComputedStyle(pw).overflowY !== 'visible' ? pw : document.scrollingElement;
    };
    const update = () => {
      const el = scroller();
      const max = el.scrollHeight - el.clientHeight;
      bar.style.transform = `scaleX(${max > 0 ? el.scrollTop / max : 0})`;
      ticking = false;
    };
    document.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { capture: true, passive: true });
    update();
  }

  if (reduced) return;

  /* Headline: words rise one after another. Walks text nodes so <em> keeps its styling. */
  document.querySelectorAll('[data-nx-split]').forEach((el) => {
    let i = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.append(part); return; }
            const outer = document.createElement('span');
            outer.className = 'nx-word';
            const inner = document.createElement('span');
            inner.textContent = part;
            inner.style.setProperty('--i', i++);
            outer.append(inner);
            frag.append(outer);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child);
        }
      });
    };
    walk(el);
    el.classList.add('is-split');
  });

  /* Scroll reveals: explicit [data-nx-reveal] plus headings and product cards below the first section. */
  if (opts.reveal !== 'false' && !designMode) {
    const auto = document.querySelectorAll(
      '#MainContent > .shopify-section:not(:first-child) :is(h2, product-card, .collection-card, .nx-reveal-auto)'
    );
    auto.forEach((el) => {
      if (el.closest('[data-nx-reveal]')) return;
      el.setAttribute('data-nx-reveal', '');
      const siblings = el.parentElement ? [...el.parentElement.children].filter((c) => c.tagName === el.tagName) : [];
      el.style.setProperty('--nx-delay', `${Math.min(siblings.indexOf(el), 6) * 80}ms`);
    });
    root.classList.add('nx-js');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px 6% 0px', threshold: 0 });
    document.querySelectorAll('[data-nx-reveal]').forEach((el) => io.observe(el));
    // Anything already on screen at load shows right away.
    requestAnimationFrame(() => {
      document.querySelectorAll('[data-nx-reveal]').forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in');
      });
    });
  } else {
    document.querySelectorAll('[data-nx-reveal]').forEach((el) => el.classList.add('is-in'));
  }

  if (!finePointer) return;

  /* 3D tilt with a moving glare on product cards and .nx-tilt elements. */
  if (opts.tilt !== 'false') {
    const tiltSel = 'product-card, .nx-tilt';
    document.addEventListener('pointermove', (e) => {
      const card = e.target.closest?.(tiltSel);
      if (!card) return;
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      card.classList.add('nx-tilting');
      card.style.setProperty('--nx-rx', `${(0.5 - y) * 8}deg`);
      card.style.setProperty('--nx-ry', `${(x - 0.5) * 10}deg`);
      card.style.setProperty('--nx-gx', `${x * 100}%`);
      card.style.setProperty('--nx-gy', `${y * 100}%`);
    });
    document.addEventListener('pointerout', (e) => {
      const card = e.target.closest?.(tiltSel);
      if (!card || card.contains(e.relatedTarget)) return;
      card.style.setProperty('--nx-rx', '0deg');
      card.style.setProperty('--nx-ry', '0deg');
      card.classList.remove('nx-tilting');
    });
  }

  /* Magnetic buttons drift toward the cursor. */
  document.querySelectorAll('[data-nx-magnet]').forEach((btn) => {
    btn.addEventListener('pointermove', (e) => {
      const r = btn.getBoundingClientRect();
      btn.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * 0.18}px, ${(e.clientY - r.top - r.height / 2) * 0.3}px)`;
    });
    btn.addEventListener('pointerleave', () => { btn.style.transform = ''; });
  });

  /* Stardust cursor trail. */
  if (opts.cursor !== 'false') {
    const canvas = document.createElement('canvas');
    canvas.className = 'nx-stardust';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.append(canvas);
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const parts = [];
    let running = false;
    let last = 0;
    const size = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
    };
    size();
    window.addEventListener('resize', size);
    const sparkle = (x, y, s) => {
      ctx.beginPath();
      ctx.moveTo(x, y - s);
      ctx.quadraticCurveTo(x, y, x + s, y);
      ctx.quadraticCurveTo(x, y, x, y + s);
      ctx.quadraticCurveTo(x, y, x - s, y);
      ctx.quadraticCurveTo(x, y, x, y - s);
      ctx.fill();
    };
    const frame = (now) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        const age = (now - p.born) / p.life;
        if (age >= 1) { parts.splice(i, 1); continue; }
        p.x += p.vx; p.y += p.vy; p.vy += 0.015;
        ctx.globalAlpha = (1 - age) * 0.9;
        ctx.fillStyle = p.gold ? '#5e5ce6' : '#64d2ff';
        sparkle(p.x * dpr, p.y * dpr, p.s * (1 - age * 0.6) * dpr);
      }
      if (parts.length) requestAnimationFrame(frame);
      else running = false;
    };
    window.addEventListener('pointermove', (e) => {
      const now = performance.now();
      if (now - last < 28) return;
      last = now;
      parts.push({
        x: e.clientX, y: e.clientY,
        vx: (Math.random() - 0.5) * 0.6, vy: (Math.random() - 0.7) * 0.6,
        s: 2 + Math.random() * 3.5, born: now, life: 650 + Math.random() * 400, gold: Math.random() < 0.45,
      });
      if (parts.length > 60) parts.shift();
      if (!running) { running = true; requestAnimationFrame(frame); }
    }, { passive: true });
  }
})();
